import { forwardRef, useId } from "react";
import type { SelectHTMLAttributes } from "react";

import { cn } from "./utils";

export interface OpcionSelector {
  valor: string;
  etiqueta: string;
}

export interface SelectorProps extends SelectHTMLAttributes<HTMLSelectElement> {
  etiqueta: string;
  ayuda?: string;
  error?: string;
  opciones: OpcionSelector[];
}

export const Selector = forwardRef<HTMLSelectElement, SelectorProps>(function Selector(
  { etiqueta, ayuda, error, opciones, className, id, ...resto },
  ref,
) {
  const idGenerado = useId();
  const idCampo = id ?? idGenerado;
  const idAyuda = ayuda ? `${idCampo}-ayuda` : undefined;
  const idError = error ? `${idCampo}-error` : undefined;
  const describedBy = [idAyuda, idError].filter(Boolean).join(" ") || undefined;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={idCampo} className="text-etiqueta text-texto">
        {etiqueta}
      </label>
      <select
        ref={ref}
        id={idCampo}
        aria-describedby={describedBy}
        aria-invalid={Boolean(error) || undefined}
        className={cn(
          "min-h-control rounded-control border border-borde bg-superficie px-3 text-cuerpo text-texto shadow-sm",
          "focus-visible:border-primario focus-visible:shadow-foco",
          "disabled:cursor-not-allowed disabled:bg-fondo disabled:text-texto-secundario/50",
          error && "border-peligro",
          className,
        )}
        {...resto}
      >
        {opciones.map((opcion) => (
          <option key={opcion.valor} value={opcion.valor}>
            {opcion.etiqueta}
          </option>
        ))}
      </select>
      {ayuda ? (
        <p id={idAyuda} className="text-nota text-texto-secundario">
          {ayuda}
        </p>
      ) : null}
      {error ? (
        <p id={idError} className="text-nota text-peligro" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
});
